import React, { useState, useEffect, useCallback } from 'react';
import { timeApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function mondayOf(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // 0 = Monday
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
const iso = (d) => d.toISOString().slice(0, 10);
const fmtRange = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export default function WeekTimesheet({ activityTypes, onChanged }) {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [entries, setEntries] = useState([]);
  const [drafts, setDrafts] = useState({}); // date -> {activity_type, hours}
  const [loading, setLoading] = useState(false);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const weekEndIso = iso(days[6]);
  const weekStartIso = iso(days[0]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const years = new Set([days[0].getFullYear(), days[6].getFullYear()]);
      const all = [];
      for (const y of years) {
        const r = await timeApi.getEntries({ year: y });
        all.push(...(r.data || []));
      }
      setEntries(all.filter((e) => e.date >= weekStartIso && e.date <= weekEndIso));
    } catch {
      toast.error('Failed to load week');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartIso, weekEndIso]);

  useEffect(() => { load(); }, [load]);

  const shift = (weeks) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + weeks * 7);
    setWeekStart(d);
  };

  const entriesFor = (dateIso) => entries.filter((e) => e.date === dateIso);
  const dayTotal = (dateIso) => entriesFor(dateIso).reduce((s, e) => s + (e.duration_hours || 0), 0);
  const weekTotal = entries.reduce((s, e) => s + (e.duration_hours || 0), 0);

  const updateHours = async (entry, val) => {
    const hours = parseFloat(val);
    if (!(hours > 0) || hours === entry.duration_hours) return;
    try {
      await timeApi.update(entry.id, { duration_hours: hours });
      load(); onChanged && onChanged();
    } catch { toast.error('Failed to update hours'); }
  };

  const del = async (id) => {
    try { await timeApi.delete(id); load(); onChanged && onChanged(); } catch { toast.error('Failed to delete'); }
  };

  const addDraft = async (dateIso) => {
    const draft = drafts[dateIso] || {};
    const hours = parseFloat(draft.hours);
    if (!(hours > 0)) { toast.error('Enter hours'); return; }
    try {
      await timeApi.create({
        activity_type: draft.activity_type || 'Onsite Labor',
        duration_hours: hours,
        date: dateIso,
      });
      setDrafts((p) => ({ ...p, [dateIso]: { activity_type: draft.activity_type || 'Onsite Labor', hours: '' } }));
      load(); onChanged && onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to add');
    }
  };

  const types = activityTypes && activityTypes.length ? activityTypes : ['Onsite Labor', 'Shop/Prep Work'];

  return (
    <div data-testid="week-timesheet">
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <div className="text-center">
          <p className="font-semibold">{fmtRange(days[0])} – {fmtRange(days[6])}</p>
          <p className="text-xs text-muted-foreground">Week total: <strong>{weekTotal.toFixed(2)} hrs</strong>{loading ? ' · loading…' : ''}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>

      <div className="space-y-3">
        {days.map((d, i) => {
          const dIso = iso(d);
          const dayEntries = entriesFor(dIso);
          const draft = drafts[dIso] || { activity_type: 'Onsite Labor', hours: '' };
          return (
            <div key={dIso} className="border rounded-lg p-3" data-testid={`week-day-${dIso}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-sm">{DAYS[i]} <span className="text-muted-foreground font-normal">{fmtRange(d)}</span></p>
                <span className="text-sm text-muted-foreground">{dayTotal(dIso).toFixed(2)} hrs</span>
              </div>
              <div className="space-y-2">
                {dayEntries.length === 0 && <p className="text-xs text-muted-foreground italic">No entries</p>}
                {dayEntries.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-sm" data-testid={`week-entry-${e.id}`}>
                    <span className="flex-1 truncate">{e.activity_type}{e.project_name ? ` · ${e.project_name}` : e.client_name ? ` · ${e.client_name}` : ''}</span>
                    {!e.is_billable && <Badge variant="secondary" className="text-[10px]">NB</Badge>}
                    {e.is_billed && <Badge className="bg-green-600 text-[10px]">Billed</Badge>}
                    <Input
                      type="number" step="0.25" defaultValue={e.duration_hours}
                      className="w-20 h-8"
                      disabled={e.is_billed}
                      onBlur={(ev) => updateHours(e, ev.target.value)}
                      data-testid={`week-hours-${e.id}`}
                    />
                    <span className="text-xs text-muted-foreground w-8">hrs</span>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600" disabled={e.is_billed} onClick={() => del(e.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              {/* Quick add for this day */}
              <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                <Select value={draft.activity_type} onValueChange={(v) => setDrafts((p) => ({ ...p, [dIso]: { ...draft, activity_type: v } }))}>
                  <SelectTrigger className="h-8 flex-1 text-xs" data-testid={`week-add-activity-${dIso}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" step="0.25" placeholder="hrs" className="w-20 h-8"
                  value={draft.hours} onChange={(e) => setDrafts((p) => ({ ...p, [dIso]: { ...draft, hours: e.target.value } }))}
                  data-testid={`week-add-hours-${dIso}`} />
                <Button size="sm" className="h-8" onClick={() => addDraft(dIso)} data-testid={`week-add-btn-${dIso}`}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
